import React, { useState, useEffect, useContext, useCallback } from 'react';
import Layout from '../../components/layouts/Main';
import { AuthContext } from '../../context/AuthContext';
import { FaSave, FaBox, FaWarehouse } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import config from '../../config';
import axios from 'axios';
import { Row, Col } from 'react-bootstrap';

const AddExistingStock = () => {
  const { loggedUser: user } = useContext(AuthContext);
  const username = user?.username;
  const domainName = username;
  const apiUrl = config.API_BASE_URL;

  const [formData, setFormData] = useState({
    product: '',
    warehouseLocationId: '',
    packingMaterialId: '',
    quantity: '',
    numberOfBags: '',
    outerPackingMaterialId: '',
    outerNumberOfBags: ''
  });

  const [products, setProducts] = useState([]);
  const [warehouseLocations, setWarehouseLocations] = useState([]);
  const [packingMaterials, setPackingMaterials] = useState([]);
  const [filteredPackingMaterials, setFilteredPackingMaterials] = useState([]);
  const [outerPackingMaterials, setOuterPackingMaterials] = useState([]);
  const [selectedPackingMaterial, setSelectedPackingMaterial] = useState(null);
  const [selectedOuterPackingMaterial, setSelectedOuterPackingMaterial] = useState(null);
  const [submissionLoading, setSubmissionLoading] = useState(false);

  const fetchProducts = useCallback(async () => {
    try {
      const response = await axios.get(`${apiUrl}/products`, {
        headers: { 'X-Subdomain': domainName },
        withCredentials: true
      });
      setProducts(response.data || []);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Failed to load products');
    }
  }, [apiUrl, domainName]);

  const fetchWarehouseLocations = useCallback(async () => {
    try {
      const response = await axios.get(`${apiUrl}/new-warehouses/storage`, {
        headers: { 'X-Subdomain': domainName },
        withCredentials: true
      });
      setWarehouseLocations(response.data || []);
    } catch (error) {
      console.error('Error fetching warehouse locations:', error);
      toast.error('Failed to load warehouse locations');
    }
  }, [apiUrl, domainName]);

  const fetchPackingMaterials = useCallback(async () => {
    try {
      const response = await axios.get(`${apiUrl}/packing-materials`, {
        headers: { 'X-Subdomain': domainName },
        withCredentials: true
      });
      const allMaterials = response.data || [];
      setPackingMaterials(allMaterials);
      setOuterPackingMaterials(allMaterials.filter(pm => pm.isOuterPacking));
    } catch (error) {
      console.error('Error fetching packing materials:', error);
      toast.error('Failed to load packing materials');
    }
  }, [apiUrl, domainName]);

  useEffect(() => {
    fetchProducts();
    fetchWarehouseLocations();
    fetchPackingMaterials();
  }, [fetchProducts, fetchWarehouseLocations, fetchPackingMaterials]);

  // Filter packing materials based on selected product
  useEffect(() => {
    if (formData.product) {
      const filtered = packingMaterials.filter(pm => !pm.isOuterPacking &&
        pm.itemWiseStuffing?.some(item =>
          item.product && item.product._id === formData.product
        )
      );
      setFilteredPackingMaterials(filtered);
    } else {
      setFilteredPackingMaterials([]);
    }
    // Reset packing material selection when product changes
    setFormData(prev => ({
      ...prev,
      packingMaterialId: '',
      numberOfBags: '',
      outerPackingMaterialId: '',
      outerNumberOfBags: ''
    }));
    setSelectedPackingMaterial(null);
    setSelectedOuterPackingMaterial(null);
  }, [formData.product, packingMaterials]);

  const handleProductChange = (productId) => {
    setFormData(prev => ({
      ...prev,
      product: productId,
      packingMaterialId: '',
      numberOfBags: '',
      outerPackingMaterialId: '',
      outerNumberOfBags: ''
    }));
    setSelectedPackingMaterial(null);
    setSelectedOuterPackingMaterial(null);
  };

  const handlePackingMaterialChange = (packingMaterialId) => {
    const pm = filteredPackingMaterials.find(pm => pm._id === packingMaterialId);
    if (pm) {
      setSelectedPackingMaterial(pm);
      setFormData(prev => ({
        ...prev,
        packingMaterialId: packingMaterialId,
        numberOfBags: '', // Reset bags count when packing material changes
        outerPackingMaterialId: '',
        outerNumberOfBags: ''
      }));
      setSelectedOuterPackingMaterial(null);
    } else {
      setSelectedPackingMaterial(null);
      setFormData(prev => ({ ...prev, packingMaterialId: '', numberOfBags: '' }));
    }
  };

  const handleOuterPackingMaterialChange = (outerPackingMaterialId) => {
    const pm = outerPackingMaterials.find(pm => pm._id === outerPackingMaterialId);
    setSelectedOuterPackingMaterial(pm);
    setFormData(prev => {
      const newFormData = { ...prev, outerPackingMaterialId };
      // Auto-calculate outer bags
      if (pm && pm.outerPackingCapacity > 0 && newFormData.numberOfBags > 0) {
        newFormData.outerNumberOfBags = Math.ceil(parseFloat(newFormData.numberOfBags) / pm.outerPackingCapacity);
      } else {
        newFormData.outerNumberOfBags = '';
      }
      return newFormData;
    });
  };

  const handleQuantityChange = (quantity) => {
    setFormData(prev => ({
      ...prev,
      quantity: quantity
    }));

    setFormData(prev => {
      const newFormData = { ...prev, quantity: quantity };

      // Auto-calculate number of bags if packing material is selected
      if (selectedPackingMaterial && quantity) {
        const stuffingItem = selectedPackingMaterial.itemWiseStuffing.find(
          item => item.product && item.product._id === newFormData.product
        );
        if (stuffingItem && stuffingItem.quantity > 0) {
          const bags = Math.ceil(parseFloat(quantity) / stuffingItem.quantity);
          newFormData.numberOfBags = bags;

          // Also calculate outer bags if selected
          if (selectedOuterPackingMaterial && selectedOuterPackingMaterial.outerPackingCapacity > 0) {
            newFormData.outerNumberOfBags = Math.ceil(bags / selectedOuterPackingMaterial.outerPackingCapacity);
          }
        }
      } else {
        newFormData.numberOfBags = '';
        newFormData.outerNumberOfBags = '';
      }
      return newFormData;
    });
  };

  const handleNumberOfBagsChange = (bags) => {
    setFormData(prev => {
      const newFormData = { ...prev, numberOfBags: bags };
      // Auto-calculate outer bags
      if (selectedOuterPackingMaterial && selectedOuterPackingMaterial.outerPackingCapacity > 0 && bags > 0) {
        newFormData.outerNumberOfBags = Math.ceil(parseFloat(bags) / selectedOuterPackingMaterial.outerPackingCapacity);
      } else {
        newFormData.outerNumberOfBags = '';
      }
      return newFormData;
    });
  };

  const validateForm = () => {
    if (!formData.product) return 'Product is required';
    if (!formData.warehouseLocationId) return 'Warehouse location is required';
    if (!formData.packingMaterialId) return 'Packing material is required';
    if (!formData.quantity || formData.quantity <= 0) return 'Valid quantity is required';
    if (!formData.numberOfBags || formData.numberOfBags <= 0) return 'Number of bags is required';
    if (formData.outerPackingMaterialId && (!formData.outerNumberOfBags || formData.outerNumberOfBags <= 0)) {
      return 'Number of outer bags is required when outer packing is selected';
    }
    return null;
  };

  const getSelectedProduct = () => {
    return products.find(p => p._id === formData.product);
  };

  const getSelectedWarehouse = () => {
    return warehouseLocations.find(w => w._id === formData.warehouseLocationId);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const error = validateForm();
    if (error) {
      toast.error(error);
      return;
    }

    try {
      setSubmissionLoading(true);

      const stuffingItem = selectedPackingMaterial.itemWiseStuffing.find(
        item => item.product && item.product._id === formData.product
      );

      const submitData = {
        product: formData.product,
        warehouseLocationId: formData.warehouseLocationId,
        packingMaterialId: formData.packingMaterialId,
        quantity: formData.quantity,
        unit: 'kg', // Assuming kg as default unit
        type: 'IN',
        source: 'MANUAL_STOCK_ADDITION', // Since this is adding existing stock
        quantityPerBag: stuffingItem ? stuffingItem.quantity : 0,
        numberOfBags: parseFloat(formData.numberOfBags),
        outerPackingMaterialId: formData.outerPackingMaterialId || null,
        outerNumberOfBags: formData.outerNumberOfBags ? parseFloat(formData.outerNumberOfBags) : null,
        bagsPerOuterBag: (formData.outerPackingMaterialId && formData.outerNumberOfBags > 0) ?
          (parseFloat(formData.numberOfBags) / parseFloat(formData.outerNumberOfBags)) :
          null,
        createdBy: user?._id
      };

      await axios.post(`${apiUrl}/inward-unloading`, submitData, {
        headers: {
          'Content-Type': 'application/json',
          'X-Subdomain': domainName
        },
        withCredentials: true
      });

      toast.success('Stock added successfully!');
      setFormData({
        product: '',
        warehouseLocationId: '',
        packingMaterialId: '',
        quantity: '',
        numberOfBags: '',
        outerPackingMaterialId: '',
        outerNumberOfBags: ''
      });
      setSelectedPackingMaterial(null);
      setSelectedOuterPackingMaterial(null);
    } catch (error) {
      console.error('Error adding stock:', error);
      toast.error(error.response?.data?.message || 'Failed to add stock');
    } finally {
      setSubmissionLoading(false);
    }
  };

  const selectedProduct = getSelectedProduct();
  const selectedWarehouse = getSelectedWarehouse();

  return (
    <Layout>
      <nav aria-label="breadcrumb" className="mb-3">
        <ol className="breadcrumb">
          <li className="breadcrumb-item"><Link to="/">Dashboard</Link></li>
          {/* <li className="breadcrumb-item"><Link to="/stock-overview">Stock</Link></li> */}
          <li className="breadcrumb-item active">Add Existing Stock</li>
        </ol>
      </nav>

      <Row className="mb-2">
        <Col>
          <h2>
            {/* <FaPlus className="me-2" /> */}
            Add Existing Stock
          </h2>
        </Col>
      </Row>

      <form onSubmit={handleSubmit}>
        <Row className='gy-3'>
          {/* Product Selection */}
          <div className="col-md-6">
            <div className="card">
              <div className="card-header bg-light">
                <h5 className="card-title mb-0">
                  <FaBox className="me-2 text-primary" />
                  Product Selection
                </h5>
              </div>
              <div className="card-body">
                <div className="mb-3">
                  <label className="form-label">Product *</label>
                  <select
                    className="form-select"
                    value={formData.product}
                    onChange={(e) => handleProductChange(e.target.value)}
                    required
                  >
                    <option value="">Select product</option>
                    {products.filter(p => p.isActive).map(product => (
                      <option key={product._id} value={product._id}>
                        {product.productName}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Warehouse Location */}
                <div className="mb-3">
                  <label className="form-label">Warehouse Storage *</label>
                  <select
                    className="form-select"
                    value={formData.warehouseLocationId}
                    onChange={(e) => setFormData(prev => ({ ...prev, warehouseLocationId: e.target.value }))}
                    required
                  >
                    <option value="">Select warehouse location</option>
                    {warehouseLocations.map(location => (
                      <option key={location._id} value={location._id}>
                        {location.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Packing Material */}
                <div className="mb-3">
                  <label className="form-label">Packing Material *</label>
                  <select
                    className="form-select"
                    value={formData.packingMaterialId}
                    onChange={(e) => handlePackingMaterialChange(e.target.value)}
                    disabled={!formData.product}
                    required
                  >
                    <option value="">
                      {formData.product ? 'Select packing material' : 'Select product first'}
                    </option>
                    {filteredPackingMaterials.map(pm => (
                      <option key={pm._id} value={pm._id} disabled={!pm.itemWiseStuffing?.some(s => s.product?._id === formData.product)}>
                        {pm.name} ({pm.size})
                      </option>
                    ))}
                  </select>
                  {formData.product && filteredPackingMaterials.length === 0 && (
                    <small className="text-warning">
                      No packing materials found for this product
                    </small>
                  )}
                </div>

                {/* Outer Packing Material */}
                <div className="mb-3">
                  <label className="form-label">Outer Packing Material (Optional)</label>
                  <select
                    className="form-select"
                    value={formData.outerPackingMaterialId}
                    onChange={(e) => handleOuterPackingMaterialChange(e.target.value)}
                    disabled={!formData.packingMaterialId}
                  >
                    <option value="">
                      {formData.packingMaterialId ? 'Select outer packing' : 'Select inner packing first'}
                    </option>
                    {outerPackingMaterials.map(pm => (
                      <option key={pm._id} value={pm._id}>
                        {pm.name} ({pm.size}) - Capacity: {pm.outerPackingCapacity} inner bags
                      </option>
                    ))}
                  </select>
                </div>

                {/* Quantity */}
                <div className="mb-3">
                  <label className="form-label">Quantity (kg) *</label>
                  <input
                    type="number"
                    className="form-control"
                    value={formData.quantity}
                    onChange={(e) => handleQuantityChange(e.target.value)}
                    placeholder="Enter quantity in kg"
                    min="0"
                    step="0.01"
                    required
                  />
                </div>

                {/* Number of Bags */}
                <div className="mb-3">
                  <label className="form-label">Number of Bags *</label>
                  <input
                    type="number"
                    className="form-control"
                    value={formData.numberOfBags}
                    onChange={(e) => handleNumberOfBagsChange(e.target.value)}
                    placeholder="Auto-calculated or enter manually"
                    min="0"
                    step="1"
                    required
                  />
                  <small className="form-text text-muted">
                    Auto-calculated based on packing material stuffing quantity. Can be edited.
                  </small>
                </div>

                {/* Number of Outer Bags */}
                <div className="mb-3">
                  <label className="form-label">Number of Outer Bags</label>
                  <input
                    type="number"
                    className="form-control"
                    value={formData.outerNumberOfBags}
                    onChange={(e) => setFormData(prev => ({ ...prev, outerNumberOfBags: e.target.value }))}
                    placeholder="Auto-calculated or enter manually"
                    min="0"
                    step="1"
                    required={!!formData.outerPackingMaterialId}
                    disabled={!formData.outerPackingMaterialId}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Summary */}
          <div className="col-md-6">
            <div className="card">
              <div className="card-header bg-light">
                <h5 className="card-title mb-0">
                  <FaWarehouse className="me-2 text-success" />
                  Stock Summary
                </h5>
              </div>
              <div className="card-body">
                {selectedProduct && (
                  <div className="mb-3">
                    <strong>Product:</strong> {selectedProduct.productName}
                  </div>
                )}

                {selectedWarehouse && (
                  <div className="mb-3">
                    <strong>Warehouse:</strong> {selectedWarehouse.label}
                  </div>
                )}

                {selectedPackingMaterial && (
                  <div className="mb-3">
                    <strong>Packing Material:</strong> {selectedPackingMaterial.name} ({selectedPackingMaterial.size})
                    {(() => {
                      const stuffingItem = selectedPackingMaterial.itemWiseStuffing.find(
                        item => item.product && item.product._id === formData.product
                      );
                      return stuffingItem ? (
                        <div className="mt-1">
                          <small className="text-muted">
                            Stuffing: {stuffingItem.quantity} {stuffingItem.unit} per bag
                          </small>
                        </div>
                      ) : null;
                    })()}
                  </div>
                )}

                {selectedOuterPackingMaterial && (
                  <div className="mb-3">
                    <strong>Outer Packing:</strong> {selectedOuterPackingMaterial.name} ({selectedOuterPackingMaterial.size})
                  </div>
                )}

                {formData.quantity && (
                  <div className="mb-3">
                    <strong>Quantity:</strong> {formData.quantity} kg
                  </div>
                )}

                {formData.numberOfBags && (
                  <div className="mb-3">
                    <strong>Number of Bags:</strong> {formData.numberOfBags}
                  </div>
                )}

                {formData.outerNumberOfBags && (
                  <div className="mb-3">
                    <strong>Number of Outer Bags:</strong> {formData.outerNumberOfBags}
                  </div>
                )}
              </div>
            </div>
          </div>
        </Row>

        {/* Actions */}
        <div className="mt-4 p-3">
              <button
                type="submit"
                className="btn btn-primary w-100"
                disabled={submissionLoading || !formData.product || !formData.warehouseLocationId || !formData.packingMaterialId || !formData.quantity}
              >
                {submissionLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    Adding Stock...
                  </>
                ) : (
                  <>
                    <FaSave className="me-1" />
                    Add Stock
                  </>
                )}
              </button>
        </div>
      </form>

    </Layout>
  );
};

export default AddExistingStock;
